import { useEffect, useState } from 'react';

/**
 * Seconds left until `retryAt`, ticking down to zero
 * (docs/07-implementation-roadmap.md F7.5).
 *
 * A static "coba lagi dalam 45 detik" stops being true the moment it is read,
 * and leaves the person estimating how long they have been looking at it. The
 * same value disables the retry button, so what the screen says and what it
 * lets you do cannot disagree.
 *
 * **The deadline is an input, not something this hook decides.** It is stamped
 * onto the `AppError` when the 429 arrives, which is what makes the count a
 * countdown rather than a stopwatch: a query error is cached, so navigating away
 * and back re-renders the same failure, and a hook that computed "now + 60s" for
 * itself would hand out a fresh minute every time.
 *
 * Counted from the wall clock rather than by decrementing: every browser this
 * app runs in throttles a background tab's timers to roughly one tick a minute,
 * so a decrementing counter would still read "40 detik" long after the wait
 * ended.
 */
export function useRetryCountdown(retryAt: number | undefined): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (retryAt === undefined) return;

    const timer = setInterval(() => {
      setNow(Date.now());
      // Nothing left to count. Cleared here rather than left running, because
      // an alert on a screen nobody closes would otherwise tick all afternoon.
      if (Date.now() >= retryAt) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [retryAt]);

  if (retryAt === undefined) return 0;
  return Math.max(0, Math.ceil((retryAt - now) / 1000));
}
